import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Truck, ShieldCheck, RotateCcw, Ruler, Star } from "lucide-react";
import SEO from "../components/SEO";
import { getProductBySlug, getProductImages, SIZES, PRODUCTS } from "../data/products";
import { useCart } from "../contexts/CartContext";

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [view, setView] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${slug}`)
      .then((r) => setReviews(r.data))
      .catch(() => {});
  }, [slug]);

  if (!product) {
    return (
      <section className="max-w-2xl mx-auto px-6 py-32 text-center">
        <p className="font-serif text-4xl">Piece not found.</p>
        <Link to="/collections" className="mt-6 inline-block font-mono-cap text-xs underline" data-testid="back-to-shop">← Back to shop</Link>
      </section>
    );
  }

  const handleAdd = () => {
    if (!size) { toast.error("Pick a size first."); return; }
    addItem(product, size, qty);
    toast.success(`Added ${product.name} (${size}) to bag.`);
  };
  const handleBuyNow = () => {
    if (!size) { toast.error("Pick a size first."); return; }
    addItem(product, size, qty);
    navigate("/checkout");
  };

  const images = getProductImages(product);
  const related = PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": images,
    "brand": { "@type": "Brand", "name": "The Dripchexk" },
    "offers": {
      "@type": "Offer",
      "url": `${window.location.origin}/product/${product.slug}`,
      "priceCurrency": "INR",
      "price": product.price,
      "availability": "https://schema.org/InStock",
    },
  };

  return (
    <>
      <SEO title={product.name} description={product.description} image={images[0]} jsonLd={jsonLd} />
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pt-8 pb-20 grid lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Image */}
        <div>
          <motion.div
            key={view}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="aspect-[3/4] bg-[#0e0e0e] overflow-hidden"
          >
            <img src={images[view]} alt={`${product.name} view ${view + 1}`} className="w-full h-full object-cover" />
          </motion.div>
          {images.length > 1 && (
            <div className={`grid gap-3 mt-3 ${images.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
              {images.map((img, i) => (
                <button
                  key={img}
                  data-testid={`view-${i}`}
                  onClick={() => setView(i)}
                  className={`aspect-square bg-[#0e0e0e] overflow-hidden border ${view === i ? "border-white" : "border-white/10"}`}
                >
                  <img src={img} alt={`thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:pt-4">
          <p className="font-mono-cap text-[10px] text-white/40">{product.category === "hoodies" ? "Hoodie" : "Tee"} · {product.color}</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2">{product.name}</h1>
          <p data-testid="product-price" className="text-2xl mt-4">₹{product.price.toLocaleString("en-IN")}</p>
          <p className="text-white/60 text-sm mt-4 leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center justify-between">
            <p className="font-mono-cap text-[10px] text-white/40">Size</p>
            <button data-testid="open-size-guide" onClick={() => setShowSizeGuide(true)} className="flex items-center gap-1 text-xs text-white/60 hover:text-white"><Ruler size={12} /> Size Guide</button>
          </div>
          <div className="grid grid-cols-5 gap-2 mt-2">
            {SIZES.map((s) => (
              <button
                key={s}
                data-testid={`size-${s}`}
                onClick={() => setSize(s)}
                className={`py-3 border text-sm transition ${size === s ? "bg-white text-black border-white" : "border-white/15 text-white/80 hover:border-white/50"}`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="mt-6">
            <p className="font-mono-cap text-[10px] text-white/40 mb-2">Quantity</p>
            <div className="inline-flex border border-white/15">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-11 hover:bg-white/5">−</button>
              <span data-testid="qty-value" className="w-12 h-11 flex items-center justify-center border-x border-white/15">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="w-10 h-11 hover:bg-white/5">+</button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-8 sticky bottom-4 lg:static z-10">
            <button data-testid="add-to-cart" onClick={handleAdd} className="border border-white text-white font-mono-cap text-xs py-4 hover:bg-white/10 transition">Add to Bag</button>
            <button data-testid="buy-now" onClick={handleBuyNow} className="bg-white text-black font-mono-cap text-xs py-4 hover:bg-white/90 transition">Buy Now</button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
            <div className="flex flex-col items-center text-center"><Truck size={16} className="text-white/60 mb-2" /><p className="text-[10px] font-mono-cap text-white/60">5-7 Day<br />Delivery</p></div>
            <div className="flex flex-col items-center text-center"><ShieldCheck size={16} className="text-white/60 mb-2" /><p className="text-[10px] font-mono-cap text-white/60">UPI / COD<br />Available</p></div>
            <div className="flex flex-col items-center text-center"><RotateCcw size={16} className="text-white/60 mb-2" /><p className="text-[10px] font-mono-cap text-white/60">7 Day<br />Refund Policy</p></div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pb-20" data-testid="product-reviews">
        <div className="flex flex-wrap items-end justify-between gap-4 border-t border-white/10 pt-10">
          <div>
            <h2 className="font-serif text-3xl">Reviews.</h2>
            {reviews.length > 0 ? (
              <div className="flex items-center gap-2 mt-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} className={(reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) >= n - 0.5 ? "text-[#FF69B4] fill-[#FF69B4]" : "text-white/20"} />
                  ))}
                </div>
                <p className="text-xs text-white/50">{(reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)} · {reviews.length} review{reviews.length > 1 ? "s" : ""}</p>
              </div>
            ) : (
              <p className="text-sm text-white/40 mt-2">No reviews yet — be the first.</p>
            )}
          </div>
          <Link to="/review" data-testid="leave-review-cta" className="font-mono-cap text-xs text-[#FF69B4] underline underline-offset-4">Bought this? Leave a review →</Link>
        </div>
        {reviews.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {reviews.map((r) => (
              <div key={r.id} data-testid="review-item" className="border border-white/10 bg-[#0a0a0a] p-5">
                <div className="flex items-center justify-between">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} className={r.rating >= n ? "text-[#FF69B4] fill-[#FF69B4]" : "text-white/20"} />
                    ))}
                  </div>
                  <p className="text-[10px] text-white/30">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
                <p className="text-sm text-white/80 mt-3 leading-relaxed">{r.text}</p>
                <p className="font-mono-cap text-[10px] text-white/40 mt-4">{r.reviewer_name} · Verified Buyer</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pb-24">
          <h2 className="font-serif text-3xl mb-6">More pieces.</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {related.map((p, i) => (
              <Link key={p.slug} to={`/product/${p.slug}`} className="block product-card">
                <div className="aspect-[3/4] bg-[#0e0e0e] overflow-hidden">
                  <img src={p.showBackFirst ? p.back : p.front} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <p className="text-sm mt-3">{p.name}</p>
                <p className="text-xs text-white/50">₹{p.price}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Size guide modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowSizeGuide(false)} data-testid="size-guide-modal">
          <div className="bg-[#0a0a0a] border border-white/10 max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <p className="font-serif text-2xl">Size Guide</p>
              <button onClick={() => setShowSizeGuide(false)} className="text-white/60">✕</button>
            </div>
            <Link to="/size-guide" className="font-mono-cap text-xs underline">Open full size guide →</Link>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetail;
